/*
    Examiner's active session list.

    PIN-210. This page used to be the entry point for administering anything: it
    created an empty session, then offered every battery and every test the
    examiner could reach through a cascader. That is the mechanism behind the
    Session vs. Session Invite mix-up — a rating scale picked here produces an
    examiner-administered session that can never be sent to the rater it was
    meant for, and nothing at that point could warn them.

    Administration now starts from the battery, which is the only place the
    battery's kind is knowable. What is left here is the way back into a session
    an examiner navigated away from mid-administration.
*/

import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Dashboard from '../dashboard';
import axios from 'axios';
import styles from '../../style/examiner/session-list-page.module.css';
import BigList from '../big-list';

const SessionListPage = () => {
  const token = useSelector((state) => state.token);
  const [sessionIDs, setSessionIDs] = useState([]);
  const [sessionInfo, setSessionInfo] = useState({});
  // Until the lookups answer, an empty list means "not loaded", not "none".
  const [loaded, setLoaded] = useState(false);

  /*
    sessionListItems:
    [
        {
            destination
            filterValue
            subtitle
            title
            uniqueKey
        },
    ]
    */
  const [sessionListItems, setSessionListItems] = useState({});

  const getSessionInfo = useCallback(() => {
    let promises = sessionIDs.map((sessionID) => {
      const data = {
        sessionID: sessionID
      };

      const config = {
        headers: {
          'Content-Type': 'application/json'
        }
      };

      return axios
        .post(process.env.REACT_APP_API_URL + '/session/info', data, config)
        .then((response) => {
          // check to make sure a data object was returned
          if (response.data == null) throw new Error('no response data');
          return { [sessionID]: response.data };
        })
        .catch((error) => {
          console.log(error);
        });
    });

    Promise.all(promises)
      .then((value) => {
        if (value.length === 0) return;

        let result = value.reduce((accumulator, currentValue) => {
          return { ...accumulator, ...currentValue };
        });
        setSessionInfo(result);
        setLoaded(true);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [sessionIDs]);

  const getSessionIDs = useCallback(() => {
    axios
      .get(process.env.REACT_APP_API_URL + '/session/getSessionIDs', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Token ' + token
        }
      })
      .then((response) => {
        if (response.data.sessionIDs == null) throw new Error('no response data');
        setSessionIDs(response.data.sessionIDs);
        if (response.data.sessionIDs.length === 0) setLoaded(true);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [token]);

  /*
    An examiner-administered rating scale lives on the questionnaire page, not the
    adaptive test page. /session/info reports the mode; older deployments do not,
    so an absent mode falls back to the adaptive page as before.
  */
  const resumeDestination = (sessionID, info) =>
    info.mode === 'questionnaire'
      ? `/examiner/questionnaire/${sessionID}`
      : `/examiner/test/${sessionID}`;

  const createSessionListItems = useCallback(() => {
    let listItems = {};
    for (const [sessionID, info] of Object.entries(sessionInfo)) {
      // A session that never started an administration is an empty shell: with
      // creation gone from this page, there is nothing to resume and nothing to
      // show.
      if (!info || !info.hasActiveTest) continue;

      // A rater is still filling in an emailed rating scale in this session;
      // resuming it would put the examiner in the same form (PIN-301), so it
      // opens view-only, to show what the rater has left (PIN-324). Once the
      // examiner has taken it over, it resumes like their own (PIN-325).
      let subtitle = `${sessionID || ''}`;
      if (info.sentToRater) subtitle = `Sent to rater · in progress · ${sessionID}`;
      else if (info.takenOver) subtitle = `Sent to rater · examiner completing · ${sessionID}`;

      listItems[sessionID] = {
        destination: info.sentToRater
          ? `/examiner/questionnaire/${sessionID}/progress`
          : resumeDestination(sessionID, info),
        filterValue: `${sessionID.toLowerCase()} ${info.examineeFirstName} ${info.examineeLastName}`,
        subtitle,
        title: `${info.examineeFirstName || ''} ${info.examineeLastName || ''}`,
        uniqueKey: sessionID
      };
    }
    setSessionListItems(listItems);
  }, [sessionInfo]);

  // init
  useEffect(() => {
    getSessionIDs();
  }, [getSessionIDs]);

  useEffect(() => {
    getSessionInfo();
  }, [getSessionInfo, sessionIDs]);

  useEffect(() => {
    createSessionListItems();
  }, [createSessionListItems]);

  return (
    <Dashboard>
      <div className={styles['card']}>
        <div className={styles['list-container']}>
          <BigList
            allowCreateNew={false}
            items={Object.values(sessionListItems)}
            title="Active Sessions"
          />
        </div>
        {loaded && Object.keys(sessionListItems).length === 0 ? (
          <p className={styles['empty-message']}>No active sessions.</p>
        ) : null}
      </div>
    </Dashboard>
  );
};

export default SessionListPage;
